import { Component, inject } from '@angular/core';
import { AppConfig } from '@app/core/app.config';
import { Check } from '@app/core/model/entities/asset/check';
import { CheckDpe, CheckType, isCheckDpe } from '@app/core/model/entities/asset/check-type';
import { ColDefContext } from '@app/shared/grid/column-builder';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';

@Component({
  selector: 'check-result-cell',
  templateUrl: './check-result-cell.component.html',
  styleUrls: ['./check-result-cell.component.scss'],
  standalone: false
})
export class CheckResultCellComponent implements ICellRendererAngularComp {

  protected params: ICellRendererParams;
  protected checkTypeCode: string;
  protected displayValue: string;
  protected displayFieldCode: string;
  protected climateLabel: string;
  protected powerLabel: string;
  protected checkTypes: { [k: string]: CheckType };

  protected readonly emptyValue = inject(AppConfig).EMPTY_FIELD_VALUE;
  protected readonly gridNullFlag = inject(AppConfig).GRID_NULL_FLAG;

  /**
   * Method used by Ag-grid to initialise the cell with grid parameters
   * There are two cases for the cell : the first one is when we receive an array of assetChecks (master grid) and the second is when we receive a simple check (detail grid),
   * so we need to check if the data received has one or more elements.
   * @param params
   */
  public agInit(params: ICellRendererParams<Check, string | CheckDpe>): void {
    this.params = params;
    this.checkTypes = params.context.checkTypes as { [k: string]: CheckType };

    const { type, value } = (params.node.group)
      // When the row is a group, only the check result is preserved, all common values are merged together.
      ? { type: null, value: params.value }
      // In ungroup row, fetch the check type from the row data.
      : { type: params.data.type, value: params.value };
    this.checkTypeCode = type;

    if (!this.isGridValueInvalid(value)) {
      if (isCheckDpe(value)) {
        this.climateLabel = value.climateLabel;
        this.powerLabel = value.powerLabel;
      } else {
        const code = (params.colDef.context as ColDefContext).fieldConfig.fieldCode;
        // CheckType.resultFields only defines severities for actual result Fields ('outcome'/'observation').
        // The computed 'result' Field isn't one of them: for non-DPE Checks it just displays the same value as
        // 'outcome', so look up severities under 'outcome' in that case too.
        this.displayFieldCode = code === 'result' ? 'outcome' : code;
        this.displayValue = value;
      }
    }
  }

  /**
   * CheckType declaring the severity of the displayed value. In group rows the Check type is unknown, so fall back
   * to any loaded CheckType declaring that value for the displayed result Field.
   * @return The CheckType to read the value severity from, or undefined if none declares it.
   */
  protected get resultCheckType(): CheckType {
    return this.checkTypes?.[this.checkTypeCode]
      ?? Object.values(this.checkTypes ?? {})
        .find(checkType => checkType.resultFields
          ?.find(resultField => resultField.fieldCode === this.displayFieldCode)?.values
          ?.some(resultValue => resultValue.value === this.displayValue));
  }

  /**
   * Method used by Ag-Grid to tell the grid to refresh this cell
   * This component does not include refresh logic
   * @returns {boolean}
   */
  public refresh(_: ICellRendererParams): boolean {
    return false;
  }

  /**
   * Check if the Value of a cell is invalid
   * @param value the value of the grid cell
   * @return true if invalid, otherwise false
   */
  private isGridValueInvalid(value: unknown): boolean {
    return value == null || value === '' || value === this.gridNullFlag;
  }
}
